import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Target,
    Sparkles,
    Globe,
    LineChart,
    Lightbulb,
    Check,
    ArrowRight,
    Star,
    TrendingUp,
    Eye,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import UpcomingWorkshops from "@/components/UpcomingWorkshops";
import Seo from "@/components/Seo";
import ClientLogos from "@/components/ClientLogos";
import ContactForm from "@/components/ContactForm";
import { useLang } from "@/context/LanguageContext";
import { caseStudies } from "@/data/caseStudies";
import { blogs } from "@/data/blogs";
import FAQ from "@/components/FAQ";
import { homeFaqs } from "@/data/faqs";
import { ORG_JSONLD, PERSON_JSONLD, faqJsonLd } from "@/lib/seo";

const solveCards = [
    { title: "Business Clarity", body: "Understand what is working, what is not, and where your business should focus next.", icon: Target },
    { title: "Brand Positioning", body: "Make your business look stronger, clearer, and more premium in the market.", icon: Sparkles },
    { title: "Digital Growth", body: "Build a better online presence through website, social media, content, and customer journey.", icon: Globe },
    { title: "Revenue Roadmap", body: "Create a practical 3, 6, or 12-month roadmap based on your business stage.", icon: LineChart },
    { title: "Execution Guidance", body: "Get direct founder-level inputs from real branding, marketing, and business experience.", icon: Lightbulb },
];

const stats = [
    { label: "Years Experience", value: "15+" },
    { label: "Clients & Projects", value: "1000+" },
    { label: "Founder of", value: "Ragi Media" },
    { label: "Based in", value: "Bengaluru" },
    { label: "Total Brand Reach", value: "2.4M+" },
    { label: "Brand · Web · UI/UX", value: "Strategy" },
];

const testimonials = [
    { name: "Manjunath R", role: "Business Owner", text: "Skilled, responsive, and genuinely passionate. The insights reshaped how I think about my brand." },
    { name: "Aishwarya", role: "Brand Founder", text: "A game-changer for our brand. Clear direction that directly boosted our reach and sales." },
    { name: "Omnath C P", role: "Entrepreneur", text: "Stunning ideas and real support in building my brand. The suggestions made a genuine difference." },
    { name: "Rohit Menon", role: "D2C Founder", text: "Cut through months of confusion in one hour. I walked out with a real action plan, not vague advice." },
];

const includes = [
    "Business deep-dive", "Brand and positioning review", "Website and digital review",
    "Lead generation direction", "Social media and content direction", "Marketing and sales ideas",
    "3-month action plan", "6-month growth direction", "Practical next steps",
];

export default function Home() {
    const featured = caseStudies.slice(0, 3);
    const latestBlogs = blogs.slice(0, 3);
    const { t } = useLang();

    return (
        <div data-testid="home-page" className="relative">
            <Seo
                title="Brand & Business Growth Consultant in Bangalore | Abhijit JK"
                description="Abhijit JK is a Bengaluru-based Brand & Business Growth Consultant, Brand Storyteller, Visual Designer, and Founder of Ragi Media. Get brand analysis, website review, social media strategy, consultation, and business growth roadmap for MSMEs and growing companies."
                keywords="brand consultant Bangalore, business growth consultant Bangalore, brand consultant Bengaluru, brand strategy consultant India, brand storyteller India, visual designer Bangalore, logo design consultant Bangalore, branding consultant for MSME, digital presence consultant, website strategy consultant, Abhijit JK, Ragi Media"
                path="/"
                jsonLd={[ORG_JSONLD, PERSON_JSONLD, faqJsonLd(homeFaqs)]}
            />

            {/* HERO */}
            <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pt-16 pb-20 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pt-20">
                <div className="lg:col-span-6">
                    <Reveal><SectionEyebrow>{t("home_hero_eyebrow")}</SectionEyebrow></Reveal>
                    <Reveal delay={0.1}>
                        <p data-testid="hero-headline" className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl">
                            {t("home_hero_pre")}{" "}
                            <span className="gold-text">{t("home_hero_c1")}</span>,{" "}
                            <span className="gold-text">{t("home_hero_c2")}</span> {t("home_hero_amp")}{" "}
                            <span className="italic font-light text-white/90">{t("home_hero_italic")}</span>
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                            {t("home_hero_sub")}
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/consultation" data-testid="hero-book-cta" className="gold-button inline-flex items-center gap-2">
                                Book Your Consultation <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <p className="mt-12 text-xs uppercase tracking-[0.26em] text-white/45">
                            {t("home_founder_line")}
                        </p>
                    </Reveal>
                </div>

                <div className="lg:col-span-6 flex items-center justify-center">
                    <Reveal delay={0.2} className="w-full">
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                            className="relative mx-auto w-full max-w-[600px]"
                        >
                            <div
                                aria-hidden
                                className="pointer-events-none absolute -inset-10 -z-10"
                                style={{ background: "radial-gradient(circle at 65% 35%, rgba(212,175,55,0.18), transparent 65%)" }}
                            />
                            <img
                                src="/images/banner-home.jpg"
                                alt="Abhijit JK — Brand & Business Growth Consultant and Founder of Ragi Media, Bengaluru"
                                data-testid="hero-founder-image"
                                loading="eager"
                                className="w-full rounded-lg object-cover"
                            />
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                className="glass glow-border absolute -left-4 top-8 hidden items-center gap-3 rounded-md px-4 py-3 sm:flex"
                            >
                                <TrendingUp size={18} className="text-gold-300" />
                                <div>
                                    <p className="font-display text-lg font-semibold gold-text leading-none">+63%</p>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">More Leads</p>
                                </div>
                            </motion.div>
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                                className="glass glow-border absolute -right-4 bottom-10 hidden items-center gap-3 rounded-md px-4 py-3 sm:flex"
                            >
                                <Eye size={18} className="text-gold-300" />
                                <div>
                                    <p className="font-display text-lg font-semibold gold-text leading-none">2.4M+</p>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Brand Reach</p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </Reveal>
                </div>
            </section>

            {/* CLIENT LOGOS */}
            <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
                <Reveal>
                    <p className="text-center label-eyebrow">{t("home_brands")}</p>
                </Reveal>
                <div className="mt-8"><ClientLogos /></div>
            </section>

            {/* SEO POSITIONING — single H1 for the page */}
            <section data-testid="home-positioning-section" className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal><SectionEyebrow>What I Do</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-6 max-w-4xl font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                        Brand &amp; Business Growth Consultant for{" "}
                        <span className="gold-text">MSMEs, Founders and Growing Businesses</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
                        I help business owners find what is blocking their growth through brand
                        analysis, website review, social media strategy, logo clarity, digital
                        presence audit, and a practical business growth roadmap.
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="mt-5 max-w-3xl text-sm leading-relaxed text-white/55 md:text-base">
                        Abhijit JK is a brand and business growth consultant based in Bengaluru,
                        India. With 15+ years of experience in branding, visual design, websites,
                        UI/UX, social media, and business communication, he helps founders and
                        business owners build stronger brand clarity, better digital presence, and
                        practical growth direction.
                    </p>
                </Reveal>
                <Reveal delay={0.25}>
                    <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/55 md:text-base">
                        Through Abhijit.pro, business owners can book a{" "}
                        <Link to="/consultation" className="text-gold-300/90 underline decoration-gold-300/30 underline-offset-4 transition hover:text-gold-300">one-to-one consultation</Link>,{" "}
                        <Link to="/consultation" className="text-gold-300/90 underline decoration-gold-300/30 underline-offset-4 transition hover:text-gold-300">one-to-one consultation</Link> or a{" "}
                        <Link to="/workshops" className="text-gold-300/90 underline decoration-gold-300/30 underline-offset-4 transition hover:text-gold-300">2-day business growth workshop</Link>.
                        The focus is to help MSMEs, manufacturers, service companies, retail
                        businesses, healthcare brands, education businesses, real estate companies,
                        and startups understand what is missing in their brand, website, social
                        media, and customer communication.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {[
                        ["Brand Analysis", "/brand-analysis"],
                        ["1-Hour Business Consultation", "/consultation"],
                        ["One-to-One Consultation", "/consultation"],
                        ["Website Review", "/website-audit"],
                        ["Social Media Strategy", "/social-media-strategy"],
                        ["Logo & Brand Identity Review", "/logo-design-consultant"],
                        ["Founder-Led Content Strategy", "/brand-storytelling-consultant"],
                        ["Business Growth Workshops", "/workshops"],
                        ["90-Day Growth Roadmap", "/consultation"],
                        ["Digital Presence Audit", "/consultation"],
                    ].map(([label, to], i) => (
                        <Reveal key={label} delay={i * 0.04}>
                            <Link
                                to={to}
                                data-testid={`home-service-${i}`}
                                className="glass glass-hover group flex h-full items-center justify-between gap-3 rounded-md px-5 py-4 text-sm text-white/80 transition hover:text-gold-300"
                            >
                                <span>{label}</span>
                                <ArrowRight size={13} className="flex-none opacity-40 transition group-hover:opacity-100" />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* WHAT I SOLVE */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal><SectionEyebrow>{t("home_solve_eyebrow")}</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                        You don&apos;t need random advice. You need a{" "}
                        <span className="gold-text">clear business direction.</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {solveCards.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <Reveal key={c.title} delay={i * 0.08}>
                                <div data-testid={`solve-card-${i}`} className="glass glass-hover h-full rounded-md p-8">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-gold-300/10 text-gold-300"><Icon size={18} /></div>
                                    <h3 className="mt-6 font-display text-xl font-medium">{c.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/60">{c.body}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* SELECTED SUCCESS STORIES */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal><SectionEyebrow>{t("home_stories_eyebrow")}</SectionEyebrow></Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                                Real work that made brands{" "}
                                <span className="gold-text">clearer & stronger.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/case-studies" data-testid="home-view-all-cases" className="ghost-button inline-flex items-center gap-2">
                            {t("btn_view_all_cases")} <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {featured.map((cs, i) => {
                        const Icon = cs.icon;
                        return (
                            <Reveal key={cs.id} delay={i * 0.08}>
                                <Link to="/case-studies" data-testid={`home-case-card-${cs.id}`} className="group block h-full">
                                    <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 220, damping: 18 }} className="glass glow-border h-full overflow-hidden rounded-lg">
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img src={cs.image} alt={cs.title} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                                        </div>
                                        <div className="p-7">
                                            <div className="flex items-center gap-2 text-gold-300"><Icon size={16} /><span className="label-eyebrow">{cs.category}</span></div>
                                            <h3 className="mt-3 font-display text-xl font-medium">{cs.title}</h3>
                                            <p className="mt-3 text-sm leading-relaxed text-white/60">{cs.metrics[0].value} {cs.metrics[0].label.toLowerCase()}, and a brand that finally stands out.</p>
                                            <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-300">Read More <ArrowRight size={12} /></span>
                                        </div>
                                    </motion.div>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* WHY ABHIJIT */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal><SectionEyebrow>{t("home_why_eyebrow")}</SectionEyebrow></Reveal>
                <div className="mt-5 grid gap-10 lg:grid-cols-12">
                    <Reveal delay={0.1} className="lg:col-span-6">
                        <h2 className="font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                            I don&apos;t give theory. I give{" "}
                            <span className="gold-text">practical business direction.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-6">
                        <p className="text-base leading-relaxed text-white/65 md:text-lg">
                            With 15+ years building brands, websites, campaigns, and
                            growth strategies, and as the founder of Ragi Media, I have
                            worked with startups, SMEs, and growing companies across
                            many industries. My consultation blends brand thinking,
                            design clarity, marketing strategy, and business
                            practicality.
                        </p>
                    </Reveal>
                </div>
                <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.06}>
                            <div data-testid={`stat-card-${i}`} className="glass glass-hover relative overflow-hidden rounded-md p-8">
                                <p className="font-display text-4xl font-semibold gold-text">{s.value}</p>
                                <p className="mt-3 text-xs uppercase tracking-[0.22em] text-white/55">{s.label}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal><SectionEyebrow>{t("home_words_eyebrow")}</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                        What business owners say about working with{" "}
                        <span className="gold-text">Abhijit JK.</span>
                    </h2>
                </Reveal>
                <div className="no-scrollbar mt-14 flex snap-x gap-6 overflow-x-auto pb-4">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.06}>
                            <div data-testid={`testimonial-card-${i}`} className="glass glow-border flex h-full w-[320px] flex-none snap-start flex-col rounded-lg p-8 sm:w-[380px]">
                                <div className="flex gap-1 text-gold-300">{[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" />)}</div>
                                <p className="mt-5 flex-1 text-base leading-relaxed text-white/75">&ldquo;{t.text}&rdquo;</p>
                                <div className="mt-6 border-t border-white/10 pt-5">
                                    <p className="font-display text-lg">{t.name}</p>
                                    <p className="text-sm text-white/50">{t.role}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* LATEST BLOGS */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal><SectionEyebrow>{t("home_latest_eyebrow")}</SectionEyebrow></Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-display text-3xl font-medium sm:text-4xl lg:text-5xl">
                                Ideas worth <span className="gold-text">your time.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/blog" data-testid="home-view-all-blogs" className="ghost-button inline-flex items-center gap-2">
                            {t("btn_view_all_blogs")} <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
                <div className="mt-14 grid gap-8 md:grid-cols-3">
                    {latestBlogs.map((b, i) => (
                        <Reveal key={b.slug} delay={i * 0.08}>
                            <Link to={`/blog/${b.slug}`} data-testid={`home-blog-${b.slug}`} className="group glass glow-border block h-full overflow-hidden rounded-lg">
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                                </div>
                                <div className="p-7">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-gold-300/80">{b.category}</p>
                                    <h3 className="mt-3 font-display text-xl font-medium leading-snug">{b.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/60">{b.excerpt}</p>
                                    <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-300">Read More <ArrowRight size={12} /></span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* MAIN OFFER — CONSULTATION */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <div className="glass glow-border relative overflow-hidden rounded-lg p-10 md:p-16">
                    <span className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-50 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 60%)" }} />
                    <div className="relative grid gap-10 lg:grid-cols-12">
                        <div className="lg:col-span-6">
                            <SectionEyebrow>{t("home_offer_eyebrow")}</SectionEyebrow>
                            <h2 className="mt-5 font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-[44px]">
                                <span className="gold-text">1-Hour Business Consultation</span>
                            </h2>
                            <p className="mt-6 text-base text-white/65 md:text-lg">
                                A focused 60-minute one-to-one session where we find what is
                                blocking your growth — brand, website, social media and customer
                                communication — and build a practical direction. Just{" "}
                                <span className="gold-text font-semibold">₹1,999</span>.
                            </p>
                            <div className="mt-6 space-y-2 text-sm text-white/65">
                                <p className="flex items-center gap-2"><Check size={14} className="text-gold-300" /> 60-Minute One-to-One Session</p>
                                <p className="flex items-center gap-2"><Check size={14} className="text-gold-300" /> Available Monday–Friday</p>
                                <p className="flex items-center gap-2"><Check size={14} className="text-gold-300" /> Slots: 8:00 AM – 9:00 AM · 9:00 AM – 10:00 AM</p>
                            </div>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link to="/consultation" data-testid="main-offer-cta" className="gold-button inline-flex items-center gap-2">
                                    Book Your Consultation <ArrowRight size={14} />
                                </Link>
                                <Link to="/workshops" data-testid="main-offer-workshops-cta" className="ghost-button inline-flex items-center gap-2">
                                    Explore Workshops <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-6">
                            <p className="label-eyebrow">You Will Walk Away With</p>
                            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                                {includes.map((it) => (
                                    <li key={it} className="flex items-start gap-2.5 text-sm text-white/75">
                                        <Check size={14} className="mt-1 flex-none text-gold-300" /><span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <UpcomingWorkshops />

            <FAQ items={homeFaqs} eyebrow="FAQ" title="Questions founders" gold="ask me first." />

            {/* CONTACT */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <Reveal><SectionEyebrow>{t("home_contact_eyebrow")}</SectionEyebrow></Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                                {t("home_contact_pre")}{" "}
                                <span className="gold-text">{t("home_contact_gold")}</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 max-w-md text-base text-white/60">
                                {t("home_contact_body")}
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <div className="glass glow-border rounded-lg p-8 md:p-10">
                            <ContactForm />
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
